// src/components/dayClose/MeaningfulReviewStep.tsx
// Step 2: Meaningful Review - 今天做了什麼有意義的事？

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Plus, Leaf } from 'lucide-react';
import { Timestamp } from 'firebase/firestore';
import { DayCloseStepProps, MeaningfulActivity } from '../../types/dayClose';
import { TimeBox } from '../../types/timeBox';
import { ScheduleItem } from '../../types/schedule';
import { useDayCloseAPI } from '../../hooks/useDayCloseAPI';
import { updateDayClose } from '../../services/api/dayClose';
import { formatTimeToString } from '../../utils/dateUtils';
import { timeBoxCategoryConfig } from '../../constants/timebox.constants';
import { logger } from '../../utils/logger';
import { useToast } from '../../contexts/ToastContext';
import { AnalyzingProgress } from './AnalyzingProgress';
import { useMediaQuery } from '../../hooks/useMediaQuery';

type ActivityItem = ScheduleItem & {
  isSelected: boolean;
  isCustom: boolean;
};

const ALL_SUGGESTIONS = [
  'Finished report',
  'Helped a friend',
  'Exercised',
  'Read a book',
  'Meditated',
  'Learned something new',
  'Planned my week',
  'Cleaned up my space'
];

export const MeaningfulReviewStep: React.FC<DayCloseStepProps> = ({
  dayCloseId,
  onComplete,
  userId,
  setLoading,
  setStepHandlers
}) => {
  const { showError } = useToast();
  const { loadTodayTimeBoxes, loadTodayEvents } = useDayCloseAPI();

  const [loading, setLoadingState] = useState(true);
  const [analyzing, setAnalyzing] = useState(false);
  const [analysisStage, setAnalysisStage] = useState<'analyzing' | 'calculating' | 'finalizing' | 'complete'>('analyzing');
  const [activities, setActivities] = useState<ActivityItem[]>([]);
  const [customActivities, setCustomActivities] = useState<string[]>([]);
  const [newActivityText, setNewActivityText] = useState('');
  
  const [shuffledSuggestions, setShuffledSuggestions] = useState<string[]>([]);
  const isMobile = useMediaQuery('(max-width: 767px)');

  useEffect(() => {
    const shuffled = [...ALL_SUGGESTIONS];
    // Fisher-Yates shuffle
    for (let i = shuffled.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
    }
    setShuffledSuggestions(shuffled);
  }, []); // Runs once on mount

  const suggestionsToShow = useMemo(() => {
    const count = isMobile ? 3 : 4;
    return shuffledSuggestions.slice(0, count);
  }, [shuffledSuggestions, isMobile]);

  // 提取重複的活動列表構建邏輯
  const buildActivityList = useCallback((): MeaningfulActivity[] => {
    const selectedActivities: MeaningfulActivity[] = activities
      .filter(a => a.isSelected)
      .map(a => ({
        sourceId: a.id,
        sourceType: a.itemType === 'timebox' ? ('timeBox' as const) : ('event' as const),
        description: a.title,
        lifeDimension: 'work' as const,
        lifeDimensionImpact: ''
      }));

    const customActivityItems: MeaningfulActivity[] = customActivities.map((text, idx) => ({
      sourceType: 'custom' as const,
      sourceId: `custom-${Date.now()}-${idx}`,
      description: text,
      lifeDimension: 'work' as const,
      lifeDimensionImpact: ''
    }));

    return [...selectedActivities, ...customActivityItems];
  }, [activities, customActivities]);

  // 載入當天活動
  useEffect(() => {
    const loadActivities = async () => {
      try {
        setLoadingState(true);

        // 並行加載 TimeBoxes 和 Events
        const [timeBoxes, events] = await Promise.all([
          loadTodayTimeBoxes(userId),
          loadTodayEvents(userId)
        ]);

        // 過濾 TimeBoxes：排除 rest
        const filteredTimeBoxes = timeBoxes.filter(
          tb => tb.category !== 'rest' && tb.status !== 'cancelled'
        );

        // 過濾 Events：排除 All day
        const filteredEvents = events.filter(
          e => e.timeType === 'timed'
        );

        // 合併並轉換為 ActivityItem
        const allActivities: ActivityItem[] = [
          ...filteredTimeBoxes.map(tb => ({
            ...tb,
            isSelected: true,  // 預設勾選
            isCustom: false
          })),
          ...filteredEvents.map(e => ({
            ...e,
            isSelected: true,  // 預設勾選
            isCustom: false
          }))
        ];

        // 按時間排序
        allActivities.sort((a, b) =>
          a.startTime.getTime() - b.startTime.getTime()
        );

        setActivities(allActivities);
      } catch (error) {
        logger.error('MeaningfulReview', 'Failed to load activities', error);
        showError('Load Failed', 'Failed to load today\'s activities');
      } finally {
        setLoadingState(false);
      }
    };

    loadActivities();
  }, [dayCloseId, userId, loadTodayTimeBoxes, loadTodayEvents, showError]);

  // 切換活動選擇
  const toggleActivity = (index: number) => {
    setActivities(prev => {
      const newActivities = [...prev];
      newActivities[index].isSelected = !newActivities[index].isSelected;
      return newActivities;
    });
  };

  // 添加自定義活動
  const addCustomActivity = () => {
    const text = newActivityText.trim();
    if (!text) return;

    setCustomActivities(prev => [...prev, text]);
    setNewActivityText('');
  };

  // 移除自定義活動
  const removeCustomActivity = (index: number) => {
    setCustomActivities(prev => prev.filter((_, i) => i !== index));
  };

  // 保存並繼續（呼叫後端 AI 分析）
  const handleContinue = useCallback(async () => {
    try {
      setLoading?.(true);
      setAnalyzing(true);
      setAnalysisStage('analyzing');

      // 構建活動列表
      const allActivities = buildActivityList();

      // 呼叫後端 AI 分析
      const { analyzeActivities } = await import('../../services/api/dayCloseActivitiesAnalysis');
      const ObjectiveService = (await import('../../features/goal/services/objectiveService')).default;

      logger.info('MeaningfulReview', 'Calling AI analysis', {
        activitiesCount: allActivities.length
      });

      // 階段 1: 分析活動
      setAnalysisStage('analyzing');
      await new Promise(resolve => setTimeout(resolve, 800)); // 短暫延遲以顯示進度

      // 獲取活躍的 Objectives
      const objectives = await ObjectiveService.getActiveObjectives();
      const activeObjectives = objectives.map(obj => ({
        id: obj.id,
        title: obj.title,
        category: obj.category || 'work'
      }));

      // 階段 2: 計算 Ripples
      setAnalysisStage('calculating');

      // 呼叫後端分析（不再傳送 dayMode，後端使用通用邏輯）
      const analysisResult = await analyzeActivities({
        activities: allActivities,
        activeObjectives,
        date: new Date().toISOString().split('T')[0]
      });

      // 階段 3: 完成
      setAnalysisStage('finalizing');
      await new Promise(resolve => setTimeout(resolve, 500));

      logger.info('MeaningfulReview', 'AI analysis completed', {
        analyzedCount: analysisResult.analyzedActivities.length,
        objectivesRipples: analysisResult.ripples.objectives?.length || 0,
        dimensionsRipples: analysisResult.ripples.lifeDimensions.length
      });

      // 保存分析結果到 DayClose
      // 向後相容：同時填充舊欄位 completedTimeBoxes
      const completedTimeBoxIds = analysisResult.analyzedActivities
        .filter(a => a.sourceType === 'timeBox')
        .map(a => a.sourceId)
        .filter((id): id is string => id !== undefined);

      await updateDayClose(dayCloseId, {
        meaningfulActivities: analysisResult.analyzedActivities,
        ripples: analysisResult.ripples,
        completedTimeBoxes: completedTimeBoxIds, // 向後相容舊系統
        aiReflectionFeedback: {
          emotionalSupport: analysisResult.aiReflectionFeedback.emotionalSupport,
          learningCapture: analysisResult.aiReflectionFeedback.keyInsight,
          inspirationalContent: {
            text: analysisResult.aiReflectionFeedback.inspirationalContent || ''
          },
          generatedAt: Timestamp.now(),
          reflectionContentHash: ''
        }
      });

      logger.info('MeaningfulReview', 'Analysis results saved');

      setAnalysisStage('complete');
      await new Promise(resolve => setTimeout(resolve, 300)); // 短暫顯示完成狀態

      onComplete();
    } catch (error) {
      logger.error('MeaningfulReview', 'Failed to analyze activities', error);
      setAnalyzing(false);

      // 使用降級處理,確保用戶可以繼續流程
      try {
        logger.warn('MeaningfulReview', 'Using fallback analysis');
        const { generateFallbackAnalysis } = await import('../../services/api/dayCloseActivitiesAnalysis');
        const ObjectiveService = (await import('../../features/goal/services/objectiveService')).default;

        // 構建活動列表
        const allActivities = buildActivityList();
        const objectives = await ObjectiveService.getActiveObjectives();

        // 使用降級分析
        const fallbackResult = generateFallbackAnalysis(allActivities, objectives);

        // 保存降級結果
        await updateDayClose(dayCloseId, {
          meaningfulActivities: fallbackResult.analyzedActivities,
          ripples: fallbackResult.ripples,
          aiReflectionFeedback: {
            emotionalSupport: fallbackResult.aiReflectionFeedback.emotionalSupport,
            learningCapture: fallbackResult.aiReflectionFeedback.keyInsight || '',
            inspirationalContent: {
              text: fallbackResult.aiReflectionFeedback.inspirationalContent || ''
            },
            generatedAt: Timestamp.now(),
            reflectionContentHash: ''
          }
        });

        logger.info('MeaningfulReview', 'Fallback analysis saved successfully');
        setAnalysisStage('complete');
        await new Promise(resolve => setTimeout(resolve, 300));
        onComplete();
      } catch (fallbackError) {
        logger.error('MeaningfulReview', 'Fallback analysis also failed', fallbackError);
        showError('Analysis Failed', 'Failed to analyze your activities. Please try again.');
        setAnalyzing(false);
      }
    } finally {
      setLoading?.(false);
    }
  }, [buildActivityList, dayCloseId, userId, onComplete, setLoading, showError]);

  // 註冊步驟處理函數到 Modal
  useEffect(() => {
    if (setStepHandlers) {
      setStepHandlers({
        handleSave: handleContinue
      });
    }

    return () => {
      if (setStepHandlers) {
        setStepHandlers({});
      }
    };
  }, [handleContinue, setStepHandlers]);

  // 通用問題文字（移除了 dayMode 依賴）
  const question = 'What meaningful things did you do today?';

  // 顯示載入狀態
  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-sky-500 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading today's activities...</p>
        </div>
      </div>
    );
  }

  // 顯示分析進度
  if (analyzing) {
    return <AnalyzingProgress stage={analysisStage} />;
  }

  const selectedCount = activities.filter(a => a.isSelected).length + customActivities.length;

  return (
    <div className="max-w-3xl mx-auto px-2 md:px-4 py-4 md:py-6">
      {/* 標題 */}
      <div className="mb-6 px-2 md:px-0">
        <h2 className="text-xl font-semibold text-gray-900 mb-2">
          {question}
        </h2>
        <p className="text-sm text-gray-500">
          Select activities that were meaningful today, or add your own.
        </p>
      </div>

      {/* 活動列表 */}
      <div className="space-y-2 md:space-y-3 mb-6">
        {activities.length === 0 ? (
          <div className="text-center py-12 px-4">
            {/* 圖示 */}
            <div className="w-16 h-16 mx-auto mb-6 flex items-center justify-center">
              <Leaf className="w-12 h-12 text-gray-400" />
            </div>
            <p className="text-gray-400 font-medium">Log what stood out today</p>
          </div>
        ) : (
          activities.map((activity, index) => {
            const isTimeBox = activity.itemType === 'timebox';
            const config = isTimeBox
              ? timeBoxCategoryConfig[(activity as TimeBox).category]
              : null;

            return (
              <button
                key={activity.id}
                onClick={() => toggleActivity(index)}
                className={`
                  w-full p-3 md:p-4 rounded-lg border-2 transition-all text-left
                  ${activity.isSelected
                    ? 'border-sky-500 bg-sky-50'
                    : 'border-gray-200 bg-white hover:border-gray-300'
                  }
                `}
              >
                <div className="flex items-start gap-3">
                  {/* 勾選框 */}
                  <div className={`
                    flex-shrink-0 w-5 h-5 rounded border-2 flex items-center justify-center mt-0.5
                    ${activity.isSelected
                      ? 'bg-sky-500 border-sky-500'
                      : 'border-gray-300'
                    }
                  `}>
                    {activity.isSelected && (
                      <svg className="w-3 h-3 text-white" fill="none" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" viewBox="0 0 24 24" stroke="currentColor">
                        <path d="M5 13l4 4L19 7"></path>
                      </svg>
                    )}
                  </div>

                  {/* 內容 */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="text-sm text-gray-500">
                        {formatTimeToString(activity.startTime)} - {formatTimeToString(activity.endTime)}
                      </span>
                      {config && (
                        <span className={`text-xs px-2 py-0.5 rounded-full ${config.color}`}>
                          {config.label}
                        </span>
                      )}
                    </div>
                    <p className="font-medium text-gray-900">
                      {activity.title}
                    </p>
                  </div>
                </div>
              </button>
            );
          })
        )}

        {/* 自定義活動 */}
        {customActivities.map((text, index) => (
          <div
            key={`custom-${index}`}
            className="p-3 md:p-4 rounded-lg border-2 border-sky-500 bg-sky-50"
          >
            <div className="flex items-start gap-3">
              <div className="flex-shrink-0 w-5 h-5 rounded border-2 bg-sky-500 border-sky-500 flex items-center justify-center mt-0.5">
                <svg className="w-3 h-3 text-white" fill="none" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" viewBox="0 0 24 24" stroke="currentColor">
                  <path d="M5 13l4 4L19 7"></path>
                </svg>
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1">
                  <span className="text-xs px-2 py-0.5 rounded-full bg-sky-100 text-sky-700">
                    Custom
                  </span>
                </div>
                <p className="font-medium text-gray-900">{text}</p>
              </div>
              <button
                onClick={() => removeCustomActivity(index)}
                className="flex-shrink-0 text-gray-400 hover:text-gray-600"
              >
                <svg className="w-5 h-5" fill="none" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" viewBox="0 0 24 24" stroke="currentColor">
                  <path d="M6 18L18 6M6 6l12 12"></path>
                </svg>
              </button>
            </div>
          </div>
        ))}
      </div>

      {/* 添加自定義活動 */}
      <div className="mb-6 px-2 md:px-0">
        <div className="flex flex-wrap justify-start gap-2 mb-4">
          {suggestionsToShow.map(
            (suggestion, index) => (
              <button
                key={index}
                onClick={() => setNewActivityText(suggestion)}
                className="px-3 py-1 text-sm rounded-full bg-gray-100 text-gray-700 hover:bg-gray-200"
              >
                {suggestion}
              </button>
            )
          )}
        </div>
        <div className="flex gap-2">
          <input
            type="text"
            value={newActivityText}
            onChange={(e) => setNewActivityText(e.target.value)}
            onKeyDown={(e) => { if (!e.nativeEvent.isComposing && e.key === 'Enter') addCustomActivity(); }}
            placeholder="Add other meaningful activity..."
            className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-sky-500"
          />
          <button
            onClick={addCustomActivity}
            disabled={!newActivityText.trim()}
            className="px-4 py-2 bg-sky-500 text-white rounded-lg hover:bg-sky-600 disabled:bg-gray-300 disabled:cursor-not-allowed flex items-center gap-2"
          >
            <Plus className="w-4 h-4" />
            Add
          </button>
        </div>
      </div>

      {/* 底部統計 */}
      <div className="border-t pt-4 px-2 md:px-0">
        <p className="text-sm text-gray-600">
          {selectedCount} {selectedCount === 1 ? 'activity' : 'activities'} selected
        </p>
      </div>
    </div>
  );
};
